import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { Calendar, LogOut, User } from 'lucide-react';
import ClientHome from './ClientHome';
import ClientPlanning from './ClientPlanning';
import ClientLayout from './ClientLayout';
import ClientAccessDenied from './ClientAccessDenied';
import { isBeneficiaryUser } from '../utils/userRole';

function ClientDashboard({ db }) {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAuthentication = async () => {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        navigate('/client-login');
        return;
      }

      // Vérifier si c'est un bénéficiaire (nouveau système)
      const isBeneficiary = await isBeneficiaryUser(db, user.uid);
      const isOldClient = user.email.startsWith('client_');

      if (!isBeneficiary && !isOldClient) {
        navigate('/client-login');
        return;
      }

      // Pour les bénéficiaires, vérifier qu'ils ont accès au clientId de l'URL
      if (isBeneficiary) {
        await verifyBeneficiaryAccess(user.uid, clientId);
      } else {
        // Pour l'ancien système, trouver le client par son UID Firebase
        await findClientByAuthUid(user.uid);
      }
    };

    checkAuthentication();
  }, [navigate, db, clientId]);

  const verifyBeneficiaryAccess = async (beneficiaryId, urlClientId) => {
    try {
      // Vérifier que l'employé bénéficiaire a bien le clientId correspondant
      const employeeDoc = await getDoc(doc(db, 'employees', beneficiaryId));

      if (employeeDoc.exists()) {
        const employeeData = employeeDoc.data();
        const authorizedClientId = employeeData.clientId;

        if (authorizedClientId === urlClientId) {
          // Charger les données du client depuis l'URL
          await loadClientData(urlClientId);
        } else {
          setError('Vous n\'avez pas accès à ce client');
          setLoading(false);
        }
      } else {
        setError('Compte bénéficiaire introuvable');
        setLoading(false);
      }
    } catch (error) {
      setError('Erreur lors de la vérification des permissions');
      setLoading(false);
    }
  };

  const findClientByAuthUid = async (authUid) => {
    try {
      // Pour les clients (ancien système), chercher par authUid
      const clientsRef = collection(db, 'clients');
      const q = query(clientsRef, where('authUid', '==', authUid));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const clientDoc = querySnapshot.docs[0];
        const clientData = {
          id: clientDoc.id,
          ...clientDoc.data()
        };
        
        // Vérifier que l'ID du client correspond à celui dans l'URL (insensible à la casse)
        if (clientData.id.toLowerCase() !== clientId.toLowerCase()) {
          setError('Accès non autorisé à ce dashboard client');
          return;
        }
        
        // Vérifier si le client a accès
        // Si hasAuthAccess est explicitement false, refuser l'accès
        // Si hasAuthAccess n'est pas défini (undefined), autoriser l'accès par défaut
        if (clientData.hasAuthAccess === false) {
          setError('access_denied');
        } else {
          setClient(clientData);
        }
      } else {
        setError('Client non trouvé');
      }
    } catch (error) {
      setError('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadClientData = async (id) => {
    try {
      const clientDoc = await getDoc(doc(db, 'clients', id));
      if (clientDoc.exists()) {
        setClient({
          id: clientDoc.id,
          ...clientDoc.data()
        });
      } else {
        setError('Client non trouvé');
      }
    } catch (error) {
      setError('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      navigate('/client-login');
    } catch (error) {
      // Erreur silencieuse lors de la déconnexion
    }
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement...</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (error) {
    if (error === 'access_denied') {
      return <ClientAccessDenied />;
    }
    
    return (
      <ClientLayout>
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="text-red-600 text-2xl mb-4">⚠️</div>
            <p className="text-gray-800 mb-4">{error}</p>
            <button
              onClick={() => navigate('/client-login')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Retour à la connexion
            </button>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (!client) {
    return (
      <ClientLayout>
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <p className="text-gray-800">Client non trouvé</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientHome db={db} client={client} />
  );
}

export default ClientDashboard;
